import { useCallback } from 'react';

import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import { CustomCellEditor, CustomCellProps as Props } from '../types';

import { Grid } from '@simplywallst/ui-core';

import { TickerSearch, useTickerSearch } from './components/TickerSearch';

import { drawCellError, isCustomCellOfKind } from '../utils';
import { getCellData, getCurrencyISO } from './utils';

import type { TickerCell } from './types';

import { kind } from './types';

const Editor: CustomCellEditor<TickerCell> = ({
  value: cell,
  initialValue,
  onFinishedEditing,
}: Props<TickerCell>) => {
  const { searchQuery, setSearchQuery, listings, status } = useTickerSearch(
    initialValue ?? cell.data.value
  );

  const handleSelect = useCallback(
    (id: string) => {
      const selectedCompany = listings.find((listing) => listing.id === id);
      if (!selectedCompany) return;

      onFinishedEditing(
        {
          ...cell,
          data: getCellData({
            ...cell.data,
            name: selectedCompany.name,
            uniqueSymbol: selectedCompany.uniqueSymbol,
            value: selectedCompany.uniqueSymbol,
            currencyISO: getCurrencyISO(
              selectedCompany.exchangeCountryIso || 'us'
            ),
            status: 'success',
          }),
        },
        [1, 0] // Move to the next RHS cell
      );
    },
    [cell, listings, onFinishedEditing]
  );

  return (
    <Grid paddingX="x0_75" alignItems="center" height={53}>
      <div data-theme="light">
        <TickerSearch
          options={listings}
          inputValue={searchQuery}
          onInputChange={setSearchQuery}
          onSelect={handleSelect}
          status={status}
          onCancel={() => onFinishedEditing(cell)}
        />
      </div>
    </Grid>
  );
};

export const Render: CustomRenderer<TickerCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect, requestAnimationFrame } = args;
    const { value, name, uniqueSymbol, status, error } = cell.data;

    if (status === 'success') {
      // Draw the Unique Symbol && Company Name
      ctx.fillStyle = theme.textMedium;
      ctx.fillText(
        uniqueSymbol ?? '',
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme) - 5
      );
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        name ?? '',
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme) + 10
      );
    }

    if (status === 'pending') {
      // Draw the Value
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        value ?? '',
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );

      // Draw the Spinner
      const progress = (window.performance.now() % 1000) / 1000;

      const x = rect.x + rect.width / 2;
      const y = rect.y + rect.height / 2;
      ctx.beginPath();
      ctx.arc(
        x,
        y,
        Math.min(12, rect.height / 6),
        Math.PI * 2 * progress,
        Math.PI * 2 * progress + Math.PI * 1.5
      );

      ctx.strokeStyle = theme.textMedium;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.lineWidth = 1;

      requestAnimationFrame();
    }

    if (status === 'error') {
      drawCellError({
        ctx,
        rect,
        theme,
        error: error ?? 'Error',
        value: value ?? '',
      });
    }

    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value } = cell.data;
    return (
      (value ? ctx.measureText(value).width : 0) +
      theme.cellHorizontalPadding * 2
    );
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: (pastedValue, cell) => {
    return {
      ...cell,
      ...getCellData(String(pastedValue)),
      status: 'pending',
    };
  },
};
